import { Component, Inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MD_DIALOG_DATA, MdDialogRef, MdSnackBar } from "@angular/material";
import { Router } from "@angular/router";
import Swal from "sweetalert2";
import { LicenseService } from "../../../../services/LicenseService";
import { AdalService } from "../../../../shared/adal.service";
import { AppLoaderService } from "../../../../../services/app-loader/app-loader.service";

@Component({
  selector: "renew-gis360-license",
  styles: [require('./renew-gis360-license.component.css')],
  template: require('./renew-gis360-license.component.html'),
})
export class RenewGIS360LicenseComponent {
  dialogTitle = "";
  errorMessage = "";
  submitted = false;
  isLoading = false;
  renewGIS360LicenseForm: FormGroup;
  selectedLicense;
  // License Keys of the sales order; IsSelected is toggled by clicking the row's check button (same as SCAL)
  licenseKeyDetails = [];

  constructor(
    private dialogRef: MdDialogRef<RenewGIS360LicenseComponent>,
    private formBuilder: FormBuilder,
    private licenseService: LicenseService,
    private appLoader: AppLoaderService,
    private snackBar: MdSnackBar,
    private adalService: AdalService,
    private router: Router,
    @Inject(MD_DIALOG_DATA) public dialogData: any,
  ) {
    if (!this.adalService.getCachedToken()) {
      this.dialogRef.close(null);
      this.router.navigate(["login"]);
    }
    this.dialogTitle = this.dialogData.dialogTitle;
    this.selectedLicense = this.dialogData.SelectedLicense;
    this.buildForm();
    this.getLicenseKeys();
  }

  buildForm() {
    // Customer Name starts with the current customer and can be changed for the renewal sales order
    this.renewGIS360LicenseForm = this.formBuilder.group({
      SalesOrderNo: ["", [Validators.required, Validators.maxLength(50)]],
      CustomerName: [this.selectedLicense.CustomerName, [Validators.required, Validators.maxLength(50)]],
      ExpiryDate: ["", Validators.required],
    });
  }

  getLicenseKeys() {
    this.isLoading = true;
    this.licenseService.getGIS360LicenseKeys(this.selectedLicense.LicenseMasterId).subscribe(
      (data) => {
        if (!data.IsError && data.DataObject != null) {
          this.licenseKeyDetails = data.DataObject.map((key) => Object.assign({}, key, { IsSelected: false }));
        } else {
          this.errorMessage = data.Error;
        }
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
        this.errorMessage = "Something went wrong.";
      },
    );
  }

  selectRow(key) {
    key.IsSelected = !key.IsSelected;
  }

  // API dates are "yyyy-MM-ddTHH:mm:ss"; compare as "yyyy-MM-dd" text like the date input value
  toDateText(value): string {
    return String(value).substring(0, 10);
  }

  onSave() {
    this.submitted = true;
    if (this.renewGIS360LicenseForm.invalid) {
      return;
    }
    const selectedKeys = this.licenseKeyDetails.filter((key) => key.IsSelected);
    if (selectedKeys.length == 0) {
      Swal.fire({
        title: "Warning",
        text: "Please select at least one license key.",
        showConfirmButton: true,
        confirmButtonColor: "#FFCA28",
      });
      return;
    }
    const expiryDate = this.renewGIS360LicenseForm.value.ExpiryDate;
    if (selectedKeys.some((key) => expiryDate <= this.toDateText(key.ExpiryDate))) {
      this.errorMessage = "New Expiry Date must be after the current Expiry Date of every selected license key.";
      return;
    }
    this.errorMessage = "";
    if (this.checkSalesOrderNoExists()) {
      Swal.fire({
        title: "This sales order no already exists",
        text: "Are you sure you want to use this Sales Order No?",
        showCancelButton: true,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        confirmButtonColor: "#FFCA28",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          this.renewLicense(selectedKeys);
        } else {
          this.renewGIS360LicenseForm.patchValue({
            SalesOrderNo: "",
          });
        }
      });
    } else {
      this.renewLicense(selectedKeys);
    }
  }

  // Same check as Generate: compare with the license list already loaded on the GIS360 page
  checkSalesOrderNoExists(): boolean {
    const salesOrderNo = this.renewGIS360LicenseForm.value.SalesOrderNo.trim().toUpperCase();
    const licenseList = this.dialogData.GIS360LicenseData || [];
    return licenseList.some((element) => {
      if (element.SalesOrderNo != null) {
        return salesOrderNo == element.SalesOrderNo.trim().toUpperCase();
      } else {
        return false;
      }
    });
  }

  renewLicense(selectedKeys) {
    const formValue = this.renewGIS360LicenseForm.getRawValue();
    // GIS360 keeps only the sales order no and customer name; all other billing/shipping fields stay null
    const billingShippingDetail = {
      Number: formValue.SalesOrderNo.trim(), // addBillingShippingDetails reads the sales order no from "Number"
      BillToName: formValue.CustomerName.trim(),
    };

    this.appLoader.open();
    this.licenseService.addBillingShippingDetail(billingShippingDetail).subscribe(
      (data) => {
        if (data.IsError || data.DataObject == null) {
          this.appLoader.close();
          this.errorMessage = data.Error || "Unable to save customer details.";
          return;
        }
        const payload = {
          LicenseMasterId: this.selectedLicense.LicenseMasterId,
          LicenseKeyIds: selectedKeys.map((key) => key.LicenseKeyId),
          SalesOrderNo: formValue.SalesOrderNo.trim(),
          CustomerId: data.DataObject.CustomerId,
          ShippingAddressId: data.DataObject.ShippingAddressId,
          ExpiryDate: formValue.ExpiryDate,
          CreatedBy: Number(localStorage.getItem("userid")),
        };
        this.licenseService.RenewGIS360License(payload).subscribe(
          (res) => {
            this.appLoader.close();
            if (!res.IsError && res.DataObject != null) {
              this.snackBar.open("License renewed successfully", "", { duration: 3000 });
              this.dialogRef.close(res.DataObject);
            } else {
              this.errorMessage = res.Error;
            }
          },
          (err) => {
            this.appLoader.close();
            this.errorMessage = "Something went wrong.";
          },
        );
      },
      (err) => {
        this.appLoader.close();
        this.errorMessage = "Something went wrong.";
      },
    );
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  hasError(controlName: string): boolean {
    const control = this.renewGIS360LicenseForm.get(controlName);
    return control.invalid && (control.touched || this.submitted);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/license-portal/gis360/popup/renew-gis360-license/renew-gis360-license.component.ts